/**
 * useThreadSearchController — the search box's brain (PR-C, 2026-09-09: the
 * search that suggests). One combobox: suggestions before typing (recent
 * threads, routines by next run, the last searches), grouped results as you
 * type (threads · messages · files · routines), the deep tier that runs BY
 * ITSELF once typing pauses (aborting the previous run), a retry row when it
 * fails, and full keyboard control (↑/↓ wrap, Home/End, Enter opens, Escape
 * clears then closes). Mirrors the @-mention controller's shape
 * (useThreadMentionAutocomplete): state + handlers, refs for the live values,
 * nothing rendered here. The query SURVIVES opening a hit — only × clears.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  THREAD_SEARCH_CAPS,
  emptyThreadSearchResults,
  highlightWordsFor,
  type ThreadSearchHit,
  type ThreadSearchResults,
} from '../../services/threadSearch/searchThreadCorpus';
import { matchText, rangesIn } from '../../services/search/matchText';
import type { ThreadListLang } from '../../types/threadCategories';

export interface SuggestedThread {
  id: string;
  title: string;
  unread?: boolean;
  pinned?: boolean;
  lastActiveAt?: string;
}

export interface SuggestedRoutine {
  id: string;
  threadId: string;
  name: string;
  nextRunAt?: string | null;
}

export interface ThreadSearchSuggestions {
  recentThreads: SuggestedThread[];
  routines: SuggestedRoutine[];
}

export interface ThreadSearchApi {
  /** Scopes the remembered searches; '' = remember nothing. */
  uid: string;
  lang: ThreadListLang;
  instant: (query: string) => ThreadSearchResults;
  /** null = the deep tier failed (offline, refused). Absent = no deep tier. */
  deep?: (query: string, signal: AbortSignal) => Promise<ThreadSearchHit[] | null>;
  /** What to show before typing. */
  suggest: () => ThreadSearchSuggestions;
  /** Opens a hit's thread (and scrolls to its message). Suggestions open as synthetic thread hits. */
  onOpen: (hit: ThreadSearchHit) => void;
}

export type ThreadSearchGroupKey = 'threads' | 'messages' | 'files' | 'routines';

export type SearchOption =
  | { id: string; kind: 'hit'; group: ThreadSearchGroupKey; hit: ThreadSearchHit }
  | { id: string; kind: 'recent-thread'; thread: SuggestedThread }
  | { id: string; kind: 'routine'; routine: SuggestedRoutine }
  | { id: string; kind: 'recent-search'; query: string }
  | { id: string; kind: 'retry' };

export interface SearchGroup {
  key: ThreadSearchGroupKey | 'jump-back-in' | 'suggested-routines' | 'recent-searches';
  options: SearchOption[];
}

export type DeepState = 'idle' | 'loading' | 'done' | 'failed';

export interface ThreadSearchController {
  query: string;
  trimmed: string;
  open: boolean;
  activeIndex: number;
  options: SearchOption[];
  groups: SearchGroup[];
  deepState: DeepState;
  /** True when a deep tier exists for this query length. */
  deepAvailable: boolean;
  suggestions: ThreadSearchSuggestions;
  recentSearches: string[];
  listboxId: string;
  optionId: (index: number) => string;
  setQuery: (value: string) => void;
  clear: () => void;
  onFocus: () => void;
  close: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onHover: (index: number) => void;
  choose: (index: number) => void;
  retryDeep: () => void;
}

export const INSTANT_DEBOUNCE_MS = 80;
export const DEEP_DEBOUNCE_MS = 450;
export const DEEP_MIN_QUERY = 2;
export const RECENT_SEARCHES_MAX = 5;
export const RECENT_SEARCHES_KEY_PREFIX = 'prest0n_thread_search_recent:';

const lastQueryByUid = new Map<string, string>();

/** The platform's modifier for the search shortcut ("⌘" on Apple devices, "Ctrl+" elsewhere). */
export function searchModKeyLabel(nav: { platform?: string; userAgent?: string } | undefined = typeof navigator !== 'undefined' ? navigator : undefined): string {
  const probe = `${nav?.platform ?? ''} ${nav?.userAgent ?? ''}`;
  return /Mac|iPhone|iPad|iPod/i.test(probe) ? '⌘' : 'Ctrl+';
}
let listboxSeq = 0;

export function recentSearchesKey(uid: string): string {
  return `${RECENT_SEARCHES_KEY_PREFIX}${uid}`;
}

export function readRecentSearches(uid: string): string[] {
  if (!uid || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(recentSearchesKey(uid));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((q): q is string => typeof q === 'string' && q.trim().length > 0).slice(0, RECENT_SEARCHES_MAX) : [];
  } catch {
    return [];
  }
}

export function rememberSearch(uid: string, query: string): string[] {
  const q = query.trim();
  if (!uid || !q || typeof window === 'undefined') return readRecentSearches(uid);
  const next = [q, ...readRecentSearches(uid).filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, RECENT_SEARCHES_MAX);
  try {
    window.localStorage.setItem(recentSearchesKey(uid), JSON.stringify(next));
  } catch { /* storage unavailable — the list is a convenience */ }
  return next;
}

export function forgetRecentSearches(uid: string): void {
  if (!uid || typeof window === 'undefined') return;
  try { window.localStorage.removeItem(recentSearchesKey(uid)); } catch { /* ignore */ }
}

/**
 * Merge the deep tier's hits INTO the instant results by score: a thread the
 * browser already found stays as found; a message the browser already showed
 * is not shown twice; group caps and the per-thread cap hold. Deep hits get
 * their highlight spans here (the server sends none).
 */
export function mergeDeepHits(instant: ThreadSearchResults, deep: readonly ThreadSearchHit[], query: string): ThreadSearchResults {
  const words = highlightWordsFor(query);
  const out: ThreadSearchResults = {
    threads: instant.threads.slice(),
    messages: instant.messages.slice(),
    files: instant.files.slice(),
    routines: instant.routines.slice(),
  };
  const threadIds = new Set(out.threads.map((h) => h.threadId));
  const messageKeys = new Set(out.messages.map((h) => `${h.threadId}|${h.messageId ?? h.sharedMessageId ?? h.snippet}`));
  const perThread = new Map<string, number>();
  for (const h of out.messages) perThread.set(h.threadId, (perThread.get(h.threadId) ?? 0) + 1);
  for (const raw of deep) {
    if (!raw?.threadId) continue;
    if (raw.kind === 'message') {
      const key = `${raw.threadId}|${raw.messageId ?? raw.sharedMessageId ?? raw.snippet}`;
      if (messageKeys.has(key)) continue;
      const n = perThread.get(raw.threadId) ?? 0;
      if (n >= THREAD_SEARCH_CAPS.perThread) continue;
      messageKeys.add(key);
      perThread.set(raw.threadId, n + 1);
      out.messages.push({ ...raw, ranges: raw.ranges ?? rangesIn(raw.snippet, words) });
      continue;
    }
    if (raw.kind === 'file' || raw.kind === 'routine') continue; // the browser owns those tiers
    if (threadIds.has(raw.threadId)) continue;
    threadIds.add(raw.threadId);
    const over = raw.kind === 'former-title' ? raw.snippet : raw.title;
    const m = matchText(query, over);
    out.threads.push({ ...raw, ranges: raw.ranges ?? (m ? m.ranges : rangesIn(over, words)) });
  }
  const byScoreThenNewest = (a: ThreadSearchHit, b: ThreadSearchHit) => b.score - a.score || (b.timestamp || '').localeCompare(a.timestamp || '');
  out.threads.sort(byScoreThenNewest);
  out.messages.sort(byScoreThenNewest);
  out.threads = out.threads.slice(0, THREAD_SEARCH_CAPS.threads);
  out.messages = out.messages.slice(0, THREAD_SEARCH_CAPS.messages);
  return out;
}

const EMPTY_SUGGESTIONS: ThreadSearchSuggestions = { recentThreads: [], routines: [] };

export function useThreadSearchController(api: ThreadSearchApi, opts: { variant?: 'rail' | 'sheet'; onOpened?: () => void } = {}): ThreadSearchController {
  const uid = api.uid || '';
  const variant = opts.variant ?? 'rail';
  const [query, setQueryState] = useState<string>(() => (variant === 'sheet' ? lastQueryByUid.get(uid) ?? '' : ''));
  const [focused, setFocused] = useState(false);
  const [instant, setInstant] = useState<ThreadSearchResults>(() => emptyThreadSearchResults());
  const [deepState, setDeepState] = useState<DeepState>('idle');
  const [deepHits, setDeepHits] = useState<ThreadSearchHit[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState<ThreadSearchSuggestions>(EMPTY_SUGGESTIONS);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => readRecentSearches(uid));
  const listboxId = useMemo(() => `thread-search-listbox-${++listboxSeq}`, []);
  const apiRef = useRef(api);
  apiRef.current = api;
  const onOpenedRef = useRef(opts.onOpened);
  onOpenedRef.current = opts.onOpened;
  const abortRef = useRef<AbortController | null>(null);
  const deepRunRef = useRef(0);

  const trimmed = query.trim();
  const deepAvailable = !!api.deep && trimmed.length >= DEEP_MIN_QUERY;

  // Remember the query per viewer so the sheet reopens on the last search.
  useEffect(() => {
    if (!uid) return;
    if (trimmed) lastQueryByUid.set(uid, query);
    else lastQueryByUid.delete(uid);
  }, [uid, query, trimmed]);

  // Instant tier, debounced a beat so a fast typist never pays per key.
  useEffect(() => {
    if (!trimmed) {
      setInstant(emptyThreadSearchResults());
      return undefined;
    }
    const timer = window.setTimeout(() => {
      try {
        setInstant(apiRef.current.instant(trimmed));
      } catch {
        setInstant(emptyThreadSearchResults());
      }
    }, INSTANT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed]);

  const runDeep = useCallback((q: string) => {
    const deep = apiRef.current.deep;
    if (!deep || q.length < DEEP_MIN_QUERY) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const run = deepRunRef.current + 1;
    deepRunRef.current = run;
    setDeepState('loading');
    deep(q, controller.signal).then((hits) => {
      if (deepRunRef.current !== run || controller.signal.aborted) return;
      if (hits === null) {
        setDeepHits([]);
        setDeepState('failed');
        return;
      }
      setDeepHits(hits);
      setDeepState('done');
    }).catch(() => {
      if (deepRunRef.current !== run || controller.signal.aborted) return;
      setDeepHits([]);
      setDeepState('failed');
    });
  }, []);

  // The deep tier runs by itself once typing pauses; a new query retires the previous run.
  useEffect(() => {
    abortRef.current?.abort();
    deepRunRef.current += 1;
    setDeepState('idle');
    setDeepHits([]);
    if (!apiRef.current.deep || trimmed.length < DEEP_MIN_QUERY) return undefined;
    const timer = window.setTimeout(() => runDeep(trimmed), DEEP_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed, runDeep]);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // Suggestions: read when the panel opens on an empty query.
  useEffect(() => {
    if (!focused || trimmed) return;
    try {
      setSuggestions(apiRef.current.suggest() ?? EMPTY_SUGGESTIONS);
    } catch {
      setSuggestions(EMPTY_SUGGESTIONS);
    }
    setRecentSearches(readRecentSearches(uid));
  }, [focused, trimmed, uid]);

  const results = useMemo(
    () => (deepState === 'done' && deepHits.length > 0 ? mergeDeepHits(instant, deepHits, trimmed) : instant),
    [instant, deepHits, deepState, trimmed],
  );

  const groups = useMemo<SearchGroup[]>(() => {
    if (!trimmed) {
      const out: SearchGroup[] = [];
      if (suggestions.recentThreads.length) out.push({ key: 'jump-back-in', options: suggestions.recentThreads.map((t) => ({ id: `rt:${t.id}`, kind: 'recent-thread' as const, thread: t })) });
      if (suggestions.routines.length) out.push({ key: 'suggested-routines', options: suggestions.routines.map((r) => ({ id: `rr:${r.id}`, kind: 'routine' as const, routine: r })) });
      if (recentSearches.length) out.push({ key: 'recent-searches', options: recentSearches.map((q, i) => ({ id: `rs:${i}`, kind: 'recent-search' as const, query: q })) });
      return out;
    }
    const out: SearchGroup[] = [];
    const push = (key: ThreadSearchGroupKey, hits: ThreadSearchHit[]) => {
      if (hits.length) out.push({ key, options: hits.map((hit, i) => ({ id: `${key}:${hit.threadId}:${hit.messageId ?? hit.routineId ?? hit.kind}:${i}`, kind: 'hit' as const, group: key, hit })) });
    };
    push('threads', results.threads);
    push('messages', results.messages);
    push('files', results.files);
    push('routines', results.routines);
    if (deepState === 'failed') out.push({ key: 'messages', options: [{ id: 'retry', kind: 'retry' as const }] });
    return out;
  }, [trimmed, suggestions, recentSearches, results, deepState]);

  const options = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const open = focused && (trimmed.length > 0 || options.length > 0);

  // The active row never points past the list.
  useEffect(() => {
    setActiveIndex((i) => (i >= options.length ? -1 : i));
  }, [options.length]);

  const setQuery = useCallback((value: string) => {
    setQueryState(value);
    setActiveIndex(-1);
  }, []);

  const clear = useCallback(() => {
    setQueryState('');
    setActiveIndex(-1);
    if (uid) lastQueryByUid.delete(uid);
  }, [uid]);

  const close = useCallback(() => {
    setFocused(false);
    setActiveIndex(-1);
  }, []);

  const onFocus = useCallback(() => setFocused(true), []);

  const choose = useCallback((index: number) => {
    const option = options[index];
    if (!option) return;
    const api = apiRef.current;
    if (option.kind === 'retry') {
      runDeep(trimmed);
      return;
    }
    if (option.kind === 'recent-search') {
      setQueryState(option.query);
      setActiveIndex(-1);
      return;
    }
    if (option.kind === 'recent-thread') {
      api.onOpen({ kind: 'thread', threadId: option.thread.id, title: option.thread.title, snippet: option.thread.title, score: 0 });
    } else if (option.kind === 'routine') {
      api.onOpen({ kind: 'routine', threadId: option.routine.threadId, title: option.routine.name, snippet: option.routine.name, score: 0, routineId: option.routine.id });
    } else {
      if (trimmed) setRecentSearches(rememberSearch(uid, trimmed));
      api.onOpen(option.hit);
    }
    setActiveIndex(-1);
    onOpenedRef.current?.();
  }, [options, runDeep, trimmed, uid]);

  const onHover = useCallback((index: number) => {
    setActiveIndex(index >= 0 && index < options.length ? index : -1);
  }, [options.length]);

  const retryDeep = useCallback(() => runDeep(trimmed), [runDeep, trimmed]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const n = options.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!focused) setFocused(true);
        if (n > 0) setActiveIndex((i) => (i + 1) % n);
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (!focused) setFocused(true);
        if (n > 0) setActiveIndex((i) => (i <= 0 ? n - 1 : i - 1));
        return;
      case 'Home':
        if (open && n > 0) { e.preventDefault(); setActiveIndex(0); }
        return;
      case 'End':
        if (open && n > 0) { e.preventDefault(); setActiveIndex(n - 1); }
        return;
      case 'Enter': {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < n) { choose(activeIndex); return; }
        const first = options.findIndex((o) => o.kind !== 'recent-search');
        if (first >= 0) { choose(first); return; }
        if (deepState === 'failed') runDeep(trimmed);
        return;
      }
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        if (query) clear();
        else { close(); (e.currentTarget as HTMLInputElement).blur(); }
        return;
      case 'Tab':
        close();
        return;
      default:
        return;
    }
  }, [options, focused, open, activeIndex, choose, deepState, runDeep, trimmed, query, clear, close]);

  const optionId = useCallback((index: number) => `${listboxId}-opt-${index}`, [listboxId]);

  return {
    query, trimmed, open, activeIndex, options, groups, deepState, deepAvailable, suggestions, recentSearches,
    listboxId, optionId, setQuery, clear, onFocus, close, onKeyDown, onHover, choose, retryDeep,
  };
}
